import sendMessageToSDK, { MessageName } from '../send-message-to-sdk'

import formatOptions from './format-options'
import frontendEventsLib from '@klarna/frontend-events'
import ga from '../google-analytics'

let tracker
let gaEnabled = false

/*
 * We already had problems with HPP breaking because a broken
 * version of @klarna/frontend-events. We don't want this to
 * happen every again, so we wrap the calls with this try/catch
 * and report to sentry in case something happens.
 */
function safeCall(func) {
  try {
    return func()
  } catch (err) {
    if (window.Sentry) {
      window.Sentry.captureException(err)
    }

    return null
  }
}

export function initTracker({
  eventUrl,
  environment,
  sessionId,
  correlationId,
  integrator,
  analytics
}: {
  eventUrl: string
  environment: string
  sessionId?: string
  correlationId?: string
  integrator?: string
  analytics?: { configurationId: string }
}) {
  const config = {
    client: 'hpp',
    // @ts-ignore
    // This constant will be replaced with the build version by wepback, don't change it.
    clientVersion: __VERSION__,
    sessionId: sessionId || '',
    instanceId: correlationId,
    baseUrl: eventUrl,
    environment,
    integrator
  }

  if (eventUrl) {
    tracker = safeCall(() => frontendEventsLib(config))
  }

  if (analytics && analytics.configurationId) {
    gaEnabled = true
    safeCall(() => ga.init(analytics.configurationId))
  }
}

export function trackEvent(event: TrackedEventProps, options?) {
  if (tracker) {
    safeCall(() => tracker.event(event.name, formatOptions(options)))
  }

  if (event.integrationName) {
    sendMessageToSDK(
      MessageName.CLIENT_INTERFACE_EVENTS,
      {
        name: event.integrationName,
        data: formatOptions(options)
      },
      event.shortDescription
    )
  }

  if (gaEnabled && event.sendToAnalytics) {
    safeCall(() => ga.event(event.name))
  }
}

interface TrackedEventProps {
  name: string
  shortDescription?: string
  sendToAnalytics: boolean
  integrationName?: string
}
export class TrackedEvent<TrackedEventProps> {
  public name: string
  public shortDescription?: string
  public sendToAnalytics: boolean
  public integrationName?: string
  public constructor({ name, shortDescription = null, sendToAnalytics, integrationName = null }) {
    this.name = name
    this.shortDescription = shortDescription
    this.sendToAnalytics = sendToAnalytics
    this.integrationName = integrationName
  }
}

export const EVENTS = {
  ELIGIBILITY_ASSESSMENT_MODULE_READY: new TrackedEvent({
    name: 'eligibility-assessment-module-ready',
    sendToAnalytics: true
  }),
  ELIGIBILITY_ASSESSMENT_HIDE_CANCEL_DIALOG: new TrackedEvent({
    name: 'eligibility-assessment-hide-cancel-dialog',
    sendToAnalytics: true
  }),
  ELIGIBILITY_ASSESSMENT_SHOW_CANCEL_DIALOG: new TrackedEvent({
    name: 'eligibility-assessment-show-cancel-dialog',
    sendToAnalytics: true
  }),
  ELIGIBILITY_ASSESSMENT_CANCEL_SESSION: new TrackedEvent({
    name: 'eligibility-assessment-cancel-session',
    sendToAnalytics: true
  }),
  ELIGIBILITY_ASSESSMENT_MODULE_COMPLETE: new TrackedEvent({
    name: 'eligibility-assessment-module-complete',
    sendToAnalytics: true
  }),
  ELIGIBILITY_PRE_ASSESSMENT_SUBMIT: new TrackedEvent({
    name: 'eligibility-pre-assessment-submit',
    sendToAnalytics: true
  }),
  ELIGIBILITY_PRE_ASSESSMENT_AMOUNT_CHOSEN: new TrackedEvent({
    name: 'eligibility-pre-assessment-amount-chosen',
    sendToAnalytics: true
  }),
  ELIGIBILITY_PRE_ASSESSMENT_INVALID_AMOUNT: new TrackedEvent({
    name: 'eligibility-pre-assessment-invalid-amount',
    sendToAnalytics: true
  }),
  DISTRIBUTION_MODULE_DISTRIBUTION_BACK: new TrackedEvent({
    name: 'distribution-module-distribution-back',
    shortDescription: 'Navigate back',
    sendToAnalytics: true,
    integrationName: 'distribution-back'
  }),
  DISTRIBUTION_MODULE_CONNECT: new TrackedEvent({
    name: 'distribution-module-connect',
    shortDescription: 'Connect consumer to token purchase',
    sendToAnalytics: true,
    integrationName: 'distribution-modal-connect'
  }),
  DISTRIBUTION_MODULE_METHOD_SENT: new TrackedEvent({
    name: 'distribution-module-method-sent',
    shortDescription: 'Send payment link',
    sendToAnalytics: true,
    integrationName: 'distribution-message-sent'
  }),
  DISTRIBUTION_MODULE_OPEN_CANCEL_DIALOG: new TrackedEvent({
    name: 'distribution-module-open-cancel-dialog',
    shortDescription: 'Open dialog for cancelling distribution module',
    sendToAnalytics: true,
    integrationName: 'cancellation-open'
  }),
  DISTRIBUTION_MODULE_CLOSE_CANCEL_DIALOG: new TrackedEvent({
    name: 'distribution-module-close-cancel-dialog',
    shortDescription: 'Close dialog for cancelling distribution module',
    sendToAnalytics: true,
    integrationName: 'cancellation-close'
  }),
  DISTRIBUTION_MODULE_CLOSE: new TrackedEvent({
    name: 'distribution-module-close',
    shortDescription: 'Close distribution module',
    sendToAnalytics: true,
    integrationName: 'distribution-modal-closed'
  }),
  DISTRIBUTION_MODULE_INTERACTIVE: new TrackedEvent({
    name: 'distribution-module-interactive',
    shortDescription: 'Accept interactions from user',
    sendToAnalytics: true,
    integrationName: 'ui-interactive'
  }),
  DISTRIBUTION_MODULE_STATUS_CHANGE: new TrackedEvent({
    name: 'distribution-module-status-change',
    shortDescription: 'Set distribution module status',
    sendToAnalytics: true,
    integrationName: 'distribution-modal-status-change'
  }),
  DISTRIBUTION_MODULE_MAUNAL_ID_CHECK_DECISION: new TrackedEvent({
    name: 'distribution-module-maunal-id-check-decision',
    shortDescription: 'Make decision about consumer identity',
    sendToAnalytics: true,
    integrationName: 'manual-id-decision'
  }),
  DISTRIBUTION_MODULE_CANCEL: new TrackedEvent({
    name: 'distribution-module-cancel',
    shortDescription: 'Cancel distribution module',
    sendToAnalytics: true,
    integrationName: 'cancellation-confirmation'
  }),
  DISTRIBUTION_MODULE_PAYMENT_CANCELLATION: new TrackedEvent({
    name: 'distribution-payment-cancellation',
    shortDescription: 'Cancelled payment',
    sendToAnalytics: true,
    integrationName: 'payment-cancellation'
  }),
  DISTRIBUTION_MODULE_PAYMENT_ERROR: new TrackedEvent({
    name: 'distribution-payment-error',
    shortDescription: 'Caught error in distribution module',
    sendToAnalytics: true,
    integrationName: 'payment-error'
  }),
  DISTRIBUTION_MODULE_PAYMENT_FAILURE: new TrackedEvent({
    name: 'distribution-payment-failure',
    shortDescription: 'Failure to complete payment',
    sendToAnalytics: true,
    integrationName: 'payment-failure'
  }),
  DISTRIBUTION_MODULE_METHOD_VIEW_RENDERED: new TrackedEvent({
    name: 'distribution-module-method-view-rendered',
    shortDescription: 'View distribution method screen',
    sendToAnalytics: true,
    integrationName: 'distribution-method-rendered'
  }),
  DISTRIBUTION_MODULE_KEYBOARD_INPUT_REQUEST: new TrackedEvent({
    name: 'distribution-module-keyboard-input-request',
    shortDescription: 'User typed something',
    sendToAnalytics: true,
    integrationName: 'distribution-modal-keyboard-input-request'
  }),
  DISTRIBUTION_MODULE_INPUT_STARTED: new TrackedEvent({
    name: 'distribution-module-input-started',
    shortDescription: 'User started typing',
    sendToAnalytics: true,
    integrationName: 'distribution-input-started'
  }),
  DISTRIBUTION_MODULE_METHOD_TIMEOUT: new TrackedEvent({
    name: 'distribution-module-method-timeout',
    shortDescription: 'Distribution timed out',
    sendToAnalytics: true,
    integrationName: 'distribution-message-timeout'
  }),
  DISTRIBUTION_MODULE_PAYMENT_ACCESS: new TrackedEvent({
    name: 'distribution-module-payment-access',
    shortDescription: 'Consumer has opened the payment link',
    sendToAnalytics: true,
    integrationName: 'payment-access'
  }),
  DISTRIBUTION_MODULE_WHERE_IS_TOKEN: new TrackedEvent({
    name: 'distribution-module-where-is-token',
    shortDescription: 'Show information about where consumer finds QR token',
    sendToAnalytics: true,
    integrationName: 'distribution-module-where-is-token'
  }),
  DISTRIBUTION_MODULE_WHERE_IS_CODE: new TrackedEvent({
    name: 'distribution-module-where-is-code',
    shortDescription: 'Show information about where consumer finds token code',
    sendToAnalytics: true,
    integrationName: 'distribution-module-where-is-code'
  }),
  IN_STORE_TOKENS_CLOSE_CLICKED: new TrackedEvent({
    name: 'in-store-tokens-close-clicked',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_BILLING_INFO_CLOSED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-billing-info-closed',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_BILLING_INFO_OPENED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-billing-info-opened',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_HELP_CLOSED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-help-closed',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_HELP_OPENED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-help-opened',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_CHANGE_BILLING_INFO_CLOSED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-change-billing-info-closed',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_CHANGE_BILLING_INFO_OPENED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-change-billing-info-opened',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_CARD_EXPIRED_OPENED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-card-expired-info-closed',
    sendToAnalytics: true
  }),
  IN_STORE_TOKENS_BOOTSTRAP_CARD_EXPIRED_CLOSED: new TrackedEvent({
    name: 'in-store-tokens-bootstrap-card-expired-info-opened',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_SESSION_INITIATED: new TrackedEvent({
    name: 'instant-shopping-bootstrap-session-initiated',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_OPEN_CALL: new TrackedEvent({
    name: 'instant-shopping-bootstrap-open-call',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_OPEN_CALLBACK: new TrackedEvent({
    name: 'instant-shopping-bootstrap-open-callback',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_OPEN_ERROR: new TrackedEvent({
    name: 'instant-shopping-bootstrap-open-error',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_LOAD_CALL: new TrackedEvent({
    name: 'instant-shopping-bootstrap-load-call',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_LOAD_CALLBACK: new TrackedEvent({
    name: 'instant-shopping-bootstrap-load-callback',
    sendToAnalytics: true
  }),
  INSTANT_SHOPPING_BOOTSTRAP_LOAD_ERROR: new TrackedEvent({
    name: 'instant-shopping-bootstrap-load-error',
    sendToAnalytics: true
  }),
  ORDER_SUMMARY_CLOSED: new TrackedEvent({
    name: 'order-summary-closed',
    sendToAnalytics: true
  }),
  ORDER_SUMMARY_OPENED: new TrackedEvent({
    name: 'order-summary-opened',
    sendToAnalytics: true
  }),
  TOKEN_SIGNUP_BOOTSTRAP_CONTINUE: new TrackedEvent({
    name: 'token-signup-bootstrap-continue',
    sendToAnalytics: true
  }),
  SESSION_INFO: new TrackedEvent({ name: 'session-info', sendToAnalytics: false }),
  MANUAL_IDENTIFICATION_OPENNED: new TrackedEvent({
    name: 'manual-identification-openned',
    sendToAnalytics: true
  }),
  MANUAL_IDENTIFICATION_CLOSED: new TrackedEvent({
    name: 'manual-identification-closed',
    sendToAnalytics: true
  }),
  MANUAL_IDENTIFICATION_CONFIRMED: new TrackedEvent({
    name: 'manual-identification-confirmed',
    sendToAnalytics: true
  }),
  KCO_TIMED_OUT: new TrackedEvent({ name: 'kco-timed-out', sendToAnalytics: true }),
  KCO_LOAD_CALL: new TrackedEvent({ name: 'kco-load-call', sendToAnalytics: true }),
  KCO_LOAD_CALLBACK: new TrackedEvent({ name: 'kco-load-callback', sendToAnalytics: true }),
  KP_AUTHORIZE_CANCEL_CALLED: new TrackedEvent({
    name: 'kp-authorize-cancel-called',
    sendToAnalytics: true
  }),
  KP_BUY_BUTTON_CLICKED: new TrackedEvent({
    name: 'kp-buy-button-clicked',
    sendToAnalytics: true
  }),
  KP_INVALID_ADDRESS_FLOW_GO_BACK: new TrackedEvent({
    name: 'kp-invalid-address-flow-go-back',
    sendToAnalytics: true
  }),
  KP_LOAD_CALLBACK: new TrackedEvent({ name: 'kp-load-callback', sendToAnalytics: true }),
  KP_INVALID_ADDRESS_FLOW_START: new TrackedEvent({
    name: 'kp-invalid-address-flow-start',
    sendToAnalytics: true
  }),
  KP_LOAD_CALL_ERROR: new TrackedEvent({
    name: 'kp-load-call-error',
    sendToAnalytics: true
  }),
  KP_AUTHORIZE_CALLBACK: new TrackedEvent({
    name: 'kp-authorize-callback',
    sendToAnalytics: true
  }),
  KP_FINALIZE_CALLBACK: new TrackedEvent({
    name: 'kp-finalize-callback',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_SUBMIT_CALL: new TrackedEvent({
    name: 'kp-bootstrap-express-submit-call',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_USER_ACCOUNT_SHOWED: new TrackedEvent({
    name: 'kp-bootstrap-express-user-account-showed',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_USER_ACCOUNT_TIMEDOUT: new TrackedEvent({
    name: 'kp-bootstrap-express-user-account-timedout',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_USER_ACCOUNT_LOGGED_IN: new TrackedEvent({
    name: 'kp-bootstrap-express-user-account-logged-in',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_DIALOG_HIDDEN: new TrackedEvent({
    name: 'kp-bootstrap-express-dialog-hidden',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_CANCEL_CLICKED: new TrackedEvent({
    name: 'kp-bootstrap-express-cancel-clicked',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_CANCELLATION_DIALOG_CLOSED: new TrackedEvent({
    name: 'kp-bootstrap-express-cancellation-dialog-closed',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_NAVIGATED_TO_BACK_OR_CANCEL: new TrackedEvent({
    name: 'kp-bootstrap-express-navigate-to-back-or-cancel',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_EXPRESS_BACK_BUTTON_SHOWED: new TrackedEvent({
    name: 'kp-bootstrap-express-back-button-showed',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_SUBMIT_CALL: new TrackedEvent({
    name: 'kp-bootstrap-light-submit-call',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_USER_ACCOUNT_SHOWED: new TrackedEvent({
    name: 'kp-bootstrap-light-user-account-showed',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_USER_ACCOUNT_TIMEDOUT: new TrackedEvent({
    name: 'kp-bootstrap-light-user-account-timedout',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_USER_ACCOUNT_LOGGED_IN: new TrackedEvent({
    name: 'kp-bootstrap-light-user-account-logged-in',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_DIALOG_HIDDEN: new TrackedEvent({
    name: 'kp-bootstrap-light-dialog-hidden',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_CANCEL_CLICKED: new TrackedEvent({
    name: 'kp-bootstrap-light-cancel-clicked',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_CANCELLATION_DIALOG_CLOSED: new TrackedEvent({
    name: 'kp-bootstrap-light-cancellation-dialog-closed',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_LOADING_OVERLAY_HIDDEN: new TrackedEvent({
    name: 'kp-bootstrap-light-loading-overlay-hidden',
    sendToAnalytics: true
  }),
  KP_BOOTSTRAP_LIGHT_BACK_BUTTON_SHOWED: new TrackedEvent({
    name: 'kp-bootstrap-light-back-button-showed',
    sendToAnalytics: true
  }),
  KP_AUTHORIZE_CALL: new TrackedEvent({ name: 'kp-authorize-call', sendToAnalytics: true }),
  KP_FINALIZE_CALL: new TrackedEvent({ name: 'kp-finalize-call', sendToAnalytics: true }),
  KP_SKELETON_TIMED_OUT: new TrackedEvent({
    name: 'kp-skeleton-timed-out',
    sendToAnalytics: true
  }),
  KP_TIMED_OUT: new TrackedEvent({ name: 'kp-timed-out', sendToAnalytics: true }),
  KP_PRE_INIT_CALL: new TrackedEvent({ name: 'kp-pre-init-call', sendToAnalytics: true }),
  KP_INIT_CALL: new TrackedEvent({ name: 'kp-init-call', sendToAnalytics: true }),
  KP_LOAD_CALL: new TrackedEvent({ name: 'kp-load-call', sendToAnalytics: true }),
  KP_CLICKED_CONTINUE_WELCOME_DIALOG: new TrackedEvent({
    name: 'kp-clicked-continue-welcome-dialog',
    sendToAnalytics: true
  }),
  KP_CLICKED_BACK_TO_MERCHANT_WELCOME_DIALOG: new TrackedEvent({
    name: 'kp-clicked-back-to-merchant-welcome-dialog',
    sendToAnalytics: true
  }),
  KP_PRESSED_NATIVE_BACK_BUTTON: new TrackedEvent({
    name: 'kp-pressed-native-back-button',
    sendToAnalytics: true
  }),
  KP_RECOVERY_FROM_FAILED_SUCCESS_REDIRECTION: new TrackedEvent({
    name: 'kp-recovery-from-failed-success-redirection',
    sendToAnalytics: true
  }),
  BILLING_ADDRESS_CORRECTION_CALL: new TrackedEvent({
    name: 'billing-address-correction-call',
    sendToAnalytics: true
  }),
  BILLING_ADDRESS_CORRECTION_SUCCESS: new TrackedEvent({
    name: 'billing-address-correction-success',
    sendToAnalytics: true
  }),
  BILLING_ADDRESS_CORRECTION_FAIL: new TrackedEvent({
    name: 'billing-address-correction-fail',
    sendToAnalytics: true
  }),
  CONSUMER_CLOSED_WINDOW: new TrackedEvent({
    name: 'consumer-closed-window',
    sendToAnalytics: true
  }),
  MOVING_FROM_EXPRESS_TO_NORMAL: new TrackedEvent({
    name: 'moving-from-express-to-normal',
    sendToAnalytics: true
  }),
  MANUAL_REDIRECT_LINK_SHOWN: new TrackedEvent({
    name: 'manual-redirect-link-shown',
    sendToAnalytics: true
  }),
  CONSUMER_CLICKED_ON_MANUAL_REDIRECT: new TrackedEvent({
    name: 'consumer-clicked-on-manual-redirect',
    sendToAnalytics: true
  }),
  INTERNAL_BROWSER_VIEW_CALLED: new TrackedEvent({
    name: 'internal-browser-view-called-on-redirect',
    sendToAnalytics: true
  }),
  FALLBACK_NAVIGATION_FOR_SAFARI_CALLED: new TrackedEvent({
    name: 'fallback-navigation-for-safari-called',
    sendToAnalytics: true
  }),
  HPP_FRONTEND_REDIRECTION: new TrackedEvent({
    name: 'hpp-frontend-redirection',
    sendToAnalytics: true
  }),
  HPP_FRONTEND_REDIRECTION_TO_COMPLETE_PAGE: new TrackedEvent({
    name: 'hpp-frontend-redirection-to-complete-page',
    sendToAnalytics: true
  }),
  HPP_FRONTEND_REDIRECTION_TO_FAILURE_PAGE: new TrackedEvent({
    name: 'hpp-frontend-redirection-to-failure-page',
    sendToAnalytics: true
  }),
  HPP_FRONTEND_REDIRECTION_TO_ERROR_PAGE: new TrackedEvent({
    name: 'hpp-frontend-redirection-to-error-page',
    sendToAnalytics: true
  }),
  HPP_FRONTEND_REDIRECTION_TO_BACK_PAGE: new TrackedEvent({
    name: 'hpp-frontend-redirection-to-back-page',
    sendToAnalytics: true
  }),
  HPP_FRONTEND_REDIRECTION_TO_CANCEL_PAGE: new TrackedEvent({
    name: 'hpp-frontend-redirection-to-cancel-page',
    sendToAnalytics: true
  })
}
